import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { ArrowBack, Delete, Storefront } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from './config';
import { AdminPageShell, AdminStat, AdminSurface } from './admin/AdminPageShell';

const formatWarningLines = (warnings = []) => warnings.filter(Boolean);

const buildDeletionAlert = ({ responseMessage, fallbackMessage, warnings = [] }) => {
    const warningLines = formatWarningLines(warnings);

    return {
        severity: warningLines.length > 0 ? 'warning' : 'success',
        text: responseMessage || [fallbackMessage, ...warningLines].filter(Boolean).join(' '),
    };
};

const BusinessDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [business, setBusiness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionMessage, setActionMessage] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [deleteType, setDeleteType] = useState('');
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        const fetchBusiness = async () => {
            try {
                const response = await axios.get(API_URL + `get_business.php?id=${id}`);
                setBusiness(response.data.data);
            } catch (err) {
                setError('Failed to fetch business details');
            } finally {
                setLoading(false);
            }
        };

        fetchBusiness();
    }, [id]);

    const userCount = useMemo(() => business?.users?.length ?? 0, [business]);
    const recordCount = useMemo(() => business?.records?.length ?? 0, [business]);

    const closeDialog = () => {
        setOpenDialog(false);
        setDeleteItem(null);
        setDeleteType('');
    };

    const handleDelete = async () => {
        if (!deleteItem || !deleteType) {
            return;
        }

        const itemToDelete = deleteItem;
        const itemType = deleteType;

        try {
            setActionMessage(null);

            if (itemType === 'user') {
                const response = await axios.post(
                    API_URL + 'delete_user.php',
                    { data: { id: itemToDelete.id } },
                    { withCredentials: true }
                );

                setBusiness((prev) => ({
                    ...prev,
                    users: prev.users.filter((user) => user.id !== itemToDelete.id),
                }));
                closeDialog();
                setActionMessage(buildDeletionAlert({
                    responseMessage: response.data?.message,
                    fallbackMessage: 'User deleted successfully.',
                    warnings: response.data?.warnings,
                }));
            } else if (itemType === 'record') {
                const response = await axios.post(
                    API_URL + 'delete_record.php',
                    { data: { id: itemToDelete.id } },
                    { withCredentials: true }
                );

                setBusiness((prev) => ({
                    ...prev,
                    records: prev.records.filter((record) => record.id !== itemToDelete.id),
                }));
                closeDialog();
                setActionMessage(buildDeletionAlert({
                    responseMessage: response.data?.message,
                    fallbackMessage: 'Record deleted successfully.',
                    warnings: response.data?.warnings,
                }));
            }
        } catch (err) {
            closeDialog();
            setActionMessage({
                severity: 'error',
                text: err.response?.data?.message || 'Failed to delete item.',
            });
        }
    };

    if (loading) {
        return (
            <AdminPageShell
                icon={<Storefront />}
                eyebrow="Business profile"
                title="Business Details"
                description="Loading the selected business profile."
                maxWidth="xl"
            >
                <AdminSurface sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </AdminSurface>
            </AdminPageShell>
        );
    }

    if (error) {
        return (
            <AdminPageShell
                icon={<Storefront />}
                eyebrow="Business profile"
                title="Business Details"
                description="There was a problem loading this business profile."
                maxWidth="xl"
            >
                <Alert severity="error">{error}</Alert>
            </AdminPageShell>
        );
    }

    return (
        <AdminPageShell
            icon={<Storefront />}
            eyebrow="Business profile"
            title={business.name}
            description="Review the business profile, related users, and uploaded records in the same polished admin style as the rest of the app."
            maxWidth="xl"
        >
            <Stack spacing={3}>
                {actionMessage && (
                    <Alert severity={actionMessage.severity} onClose={() => setActionMessage(null)}>
                        {actionMessage.text}
                    </Alert>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <AdminStat label="Status" value={business.approved ? 'Approved' : 'Pending'} helper="Current approval state for this business." />
                    <AdminStat label="Users" value={userCount} helper="Team members attached to this business." />
                    <AdminStat label="Records" value={recordCount} helper="Records currently stored for this business." />
                </Stack>

                <AdminSurface>
                    <Stack spacing={2.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#2d2a5a', overflowWrap: 'anywhere' }}>
                                    {business.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    Business ID: {business.id}
                                </Typography>
                            </Box>
                            <Chip
                                label={business.approved ? 'Approved' : 'Pending'}
                                color={business.approved ? 'success' : 'warning'}
                                sx={{ fontWeight: 700 }}
                            />
                        </Stack>

                        <Divider />

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                gap: 2,
                            }}
                        >
                            <DetailItem label="Category" value={business.category} />
                            <DetailItem label="Email" value={business.email} />
                            <DetailItem label="Phone" value={business.phone} />
                            <DetailItem label="Address" value={business.address} />
                            <DetailItem label="Registration date" value={business.registrationDate} />
                            <DetailItem label="Owner" value={business.owner} />
                        </Box>

                        {business.description && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
                                    Description
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#2d2a5a', overflowWrap: 'anywhere' }}>
                                    {business.description}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </AdminSurface>

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
                    <AdminSurface sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2d2a5a', mb: 2 }}>
                            Users ({userCount})
                        </Typography>
                        <List sx={{ p: 0 }}>
                            {business.users.map((user, index) => (
                                <React.Fragment key={user.id}>
                                    <ListItem
                                        disableGutters
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                onClick={() => {
                                                    setDeleteItem(user);
                                                    setDeleteType('user');
                                                    setOpenDialog(true);
                                                }}
                                            >
                                                <Delete color="error" />
                                            </IconButton>
                                        }
                                        sx={{ pr: 6 }}
                                    >
                                        <ListItemText primary={user.name} secondary={user.email} />
                                    </ListItem>
                                    {index < business.users.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))}
                            {business.users.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No users are attached to this business yet.
                                </Typography>
                            )}
                        </List>
                    </AdminSurface>

                    <AdminSurface sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2d2a5a', mb: 2 }}>
                            Records ({recordCount})
                        </Typography>
                        <List sx={{ p: 0 }}>
                            {business.records.map((record, index) => (
                                <React.Fragment key={record.id}>
                                    <ListItem
                                        disableGutters
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                onClick={() => {
                                                    setDeleteItem(record);
                                                    setDeleteType('record');
                                                    setOpenDialog(true);
                                                }}
                                            >
                                                <Delete color="error" />
                                            </IconButton>
                                        }
                                        sx={{ pr: 6 }}
                                    >
                                        <ListItemText primary={record.name} secondary={`Record ID: ${record.id}`} />
                                    </ListItem>
                                    {index < business.records.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))}
                            {business.records.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No records are available for this business yet.
                                </Typography>
                            )}
                        </List>
                    </AdminSurface>
                </Stack>

                <Box>
                    <Button
                        variant="contained"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)}
                        sx={{
                            borderRadius: '18px',
                            px: 3,
                            py: 1.2,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #4a90e2 0%, #6a5af9 55%, #9013fe 100%)',
                        }}
                    >
                        Back to List
                    </Button>
                </Box>
            </Stack>

            <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="xs">
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Alert severity="warning" sx={{ alignItems: 'flex-start' }}>
                            {deleteType === 'record'
                                ? 'Deleting a record also removes its saved QR image and any local menu file. Missing files can still result in a completed delete with warnings.'
                                : 'Deleting a user is permanent. Owner accounts cannot be deleted, and any server validation error will be shown after this dialog closes.'}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Are you sure you want to delete {deleteItem?.name || deleteItem?.title}?
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminPageShell>
    );
};

const DetailItem = ({ label, value }) => (
    <Box
        sx={{
            p: 2,
            borderRadius: '18px',
            border: '1px solid rgba(106, 90, 249, 0.12)',
            background: 'linear-gradient(180deg, rgba(248,249,255,0.95) 0%, rgba(255,255,255,0.95) 100%)',
        }}
    >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            {label}
        </Typography>
        <Typography variant="body1" sx={{ color: '#2d2a5a', fontWeight: 700, overflowWrap: 'anywhere' }}>
            {value || '-'}
        </Typography>
    </Box>
);

export default BusinessDetails;
